'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { AccessibleDialog } from '@/components/ui/accessible-dialog'
import {
  buildApartmentFloorPlanSlides,
  buildApartmentGallerySlides,
  type ApartmentGallerySlide,
  type ApartmentGalleryViewId,
  type ApartmentGalleryViews,
  type ApartmentImage,
} from '@/lib/apartments/apartmentGallery'

import { RoomGalleryViewer, type RoomGalleryViewerCopy } from './RoomGalleryViewer'

const DEFAULT_LIGHTBOX_ASPECT_RATIO = 16 / 9

function getImageAspectRatio(image: ApartmentImage | undefined): number | null {
  if (!image?.width || !image.height || image.width <= 0 || image.height <= 0) return null

  return image.width / image.height
}

function getSlideAspectRatio(slide: ApartmentGallerySlide | undefined): number | null {
  if (!slide) return null
  if (slide.kind === 'image') return getImageAspectRatio(slide.item)

  return getImageAspectRatio(slide.item.before) ?? getImageAspectRatio(slide.item.after)
}

type RoomGalleryFullscreenDialogProps = {
  activeView: ApartmentGalleryViewId
  ariaLabel: string
  carouselKey: string
  copy: RoomGalleryViewerCopy
  gallerySlideIndex: number
  floorPlanSlideIndex: number
  onActiveViewChange: (view: ApartmentGalleryViewId) => void
  onClose: () => void
  onGallerySlideIndexChange: (index: number) => void
  onFloorPlanSlideIndexChange: (index: number) => void
  open: boolean
  restoreFocusRef?: React.RefObject<HTMLElement | null>
  enabledTabs: ApartmentGalleryViewId[]
  views: ApartmentGalleryViews
}

export function RoomGalleryFullscreenDialog({
  activeView,
  ariaLabel,
  carouselKey,
  copy,
  gallerySlideIndex,
  floorPlanSlideIndex,
  onActiveViewChange,
  onClose,
  onGallerySlideIndexChange,
  onFloorPlanSlideIndexChange,
  open,
  restoreFocusRef,
  enabledTabs,
  views,
}: RoomGalleryFullscreenDialogProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [frameWidth, setFrameWidth] = useState<number | null>(null)
  const aspectRatio = useMemo(() => {
    if (activeView === 'gallery') {
      return (
        getSlideAspectRatio(buildApartmentGallerySlides(views)[gallerySlideIndex]) ??
        DEFAULT_LIGHTBOX_ASPECT_RATIO
      )
    }

    if (activeView === 'floorPlans') {
      return (
        getSlideAspectRatio(buildApartmentFloorPlanSlides(views.floorPlans)[floorPlanSlideIndex]) ??
        DEFAULT_LIGHTBOX_ASPECT_RATIO
      )
    }

    return DEFAULT_LIGHTBOX_ASPECT_RATIO
  }, [activeView, floorPlanSlideIndex, gallerySlideIndex, views])

  useEffect(() => {
    if (!open) return

    const updateFrameWidth = () => {
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const horizontalGutters = viewportWidth >= 640 ? 112 : viewportWidth * 0.1
      const width = Math.min(
        viewportWidth * 0.9,
        1280,
        viewportWidth - horizontalGutters,
        Math.max(0, viewportHeight - 144) * aspectRatio,
      )

      setFrameWidth(width)
    }

    updateFrameWidth()
    window.addEventListener('resize', updateFrameWidth)

    return () => window.removeEventListener('resize', updateFrameWidth)
  }, [aspectRatio, open])

  useEffect(() => {
    if (open) return

    const iframes = document.querySelectorAll<HTMLIFrameElement>(
      '[data-slot="dialog-content"] iframe',
    )

    for (const iframe of iframes) {
      iframe.src = 'about:blank'
    }
  }, [open])

  return (
    <AccessibleDialog
      ariaLabel={ariaLabel}
      className="bg-black/70 p-4 sm:p-6"
      contentClassName="w-auto max-h-none max-w-none p-0"
      initialFocusRef={closeButtonRef}
      onClose={onClose}
      open={open}
      restoreFocusRef={restoreFocusRef}
    >
      <div
        className="relative bg-transparent"
        style={{
          aspectRatio,
          width: frameWidth == null ? '90vw' : `${frameWidth}px`,
        }}
      >
        <RoomGalleryViewer
          activeView={activeView}
          carouselKey={`${carouselKey}-fullscreen`}
          className="size-full"
          copy={copy}
          fullscreenButtonRef={closeButtonRef}
          gallerySlideIndex={gallerySlideIndex}
          floorPlanSlideIndex={floorPlanSlideIndex}
          mode="fullscreen"
          objectFit="contain"
          onActiveViewChange={onActiveViewChange}
          onGallerySlideIndexChange={onGallerySlideIndexChange}
          onFloorPlanSlideIndexChange={onFloorPlanSlideIndexChange}
          onRequestClose={onClose}
          enabledTabs={enabledTabs}
          views={views}
        />
      </div>
    </AccessibleDialog>
  )
}
